import axios from 'axios';
import moment from 'moment';
import {
  getToolboxToken, getNcToken, isDecOrQuote,
  OTHER_FEES_BILLING_VALUE, parseAmount,
  type InsuredData, type CoverageLimitDetails, type VehicleItem, type DriverItem, type FeeItem, type PropertyItem,
} from './acordSmartScanShared';

/* Shared NowCerts submission chain — mirrors createNewInsured/createNewProspect/
 * insertQuote/createNewPolicy/insertPolicyEndorsementFee/getLobList/
 * insertCoverageAndLimits/updateSmartScan in the legacy SmartScanDataExtractPage.tsx,
 * so the live-scan review and the History preview push identical request/response
 * shapes to NowCerts. */

export interface SubmitCtx {
  insured: InsuredData;
  coverageLimitDetails: CoverageLimitDetails | null;
  propertyDetails: PropertyItem[];
  vehicleDetails: VehicleItem[];
  driverDetails: DriverItem[];
  taxesAndFees: FeeItem[];
  documentType: string;
  submissionId: string;
  isProspect: boolean;
  createNewClient: boolean;
  createQuote: boolean;
  insuredId: string;
}

export interface SubmitStepResult { label: string; success: boolean; message: string; }

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToolboxToken()}` } });
const fmtDate = (d?: string) => (d ? moment(d).format('MM/DD/YYYY') : '');
const stripMoney = (v?: string) => (v == null || v === '' ? '0' : String(v).replace(/\$/g, '').replace(/,/g, ''));

function sanitizeDollar(obj: any): any {
  if (typeof obj !== 'object' || obj === null) return obj;
  const out: any = Array.isArray(obj) ? [] : {};
  for (const key in obj) {
    if (typeof obj[key] === 'string') out[key] = obj[key].replace(/\$/g, '');
    else if (typeof obj[key] === 'object' && obj[key] !== null) out[key] = sanitizeDollar(obj[key]);
    else out[key] = obj[key];
  }
  return out;
}

const hasCoverageData = (details: any): boolean => {
  if (!details || typeof details !== 'object') return false;
  return Object.values(details).some((v) => v !== null && v !== undefined && String(v).trim() !== '');
};

async function createNewInsured(insured: InsuredData): Promise<{ ok: boolean; insuredId: string; ncUrl: string; result: SubmitStepResult }> {
  const isCommercial = insured.insuredType !== 'Personal';
  const payload = {
    commercialName: isCommercial ? insured.businessName : '',
    firstName: !isCommercial ? insured.firstName : '',
    lastName: !isCommercial ? insured.lastName : '',
    dba: '', addressLine1: insured.address, addressLine2: '',
    state: insured.state, city: insured.city, zipCode: insured.zipCode,
    eMail: insured.email, cellPhone: insured.phoneNumber, fein: '',
    insuredType: isCommercial ? 0 : 1,
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insertinsured?nowCertsToken=${getNcToken()}`, payload, authHeaders());
    return {
      ok: true, insuredId: res.data?.database_id || '', ncUrl: res.data?.newInsuredLunchUrl || '',
      result: { label: 'Insured', success: true, message: 'Insured has been created successfully in NowCerts' },
    };
  } catch (err: any) {
    const message = err?.response?.status === 400
      ? (err.response.data?.errors || 'Validation error')
      : 'Please correct NowCerts username and password.';
    return { ok: false, insuredId: '', ncUrl: '', result: { label: 'Insured', success: false, message } };
  }
}

async function createNewProspect(insured: InsuredData): Promise<{ ok: boolean; insuredId: string; ncUrl: string; result: SubmitStepResult }> {
  const isCommercial = insured.insuredType !== 'Personal';
  const payload = {
    commercialName: isCommercial ? insured.businessName : '',
    firstName: !isCommercial ? insured.firstName : '',
    lastName: !isCommercial ? insured.lastName : '',
    dba: '', addressLine1: insured.address, addressLine2: '',
    state: insured.state, city: insured.city, zipCode: insured.zipCode,
    eMail: insured.email, cellPhone: insured.phoneNumber, fein: '',
    insuredType: isCommercial ? 0 : 1,
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/createnewprospect?nowCertsToken=${getNcToken()}`, payload, authHeaders());
    return {
      ok: true, insuredId: res.data?.insuredDatabaseId || '', ncUrl: res.data?.nowCertsUrlToLaunch || '',
      result: { label: 'Prospect', success: true, message: 'Prospect has been created successfully in NowCerts.' },
    };
  } catch (err: any) {
    const message = err?.response?.status === 400
      ? (err.response.data?.errors || 'Validation error')
      : 'Please correct NowCerts username and password.';
    return { ok: false, insuredId: '', ncUrl: '', result: { label: 'Prospect', success: false, message } };
  }
}

async function insertQuote(insuredId: string, insured: InsuredData): Promise<{ ok: boolean; quoteId: string; result: SubmitStepResult }> {
  const payload = {
    insuredDatabaseId: insuredId,
    insuredFirstName: insured.firstName,
    insuredLastName: insured.lastName,
    insuredEmail: insured.email,
    effectiveDate: fmtDate(insured.effectiveDate),
    expirationDate: fmtDate(insured.expirationDate),
    bindDate: '',
    insuredCommercialName: insured.businessName,
    lineOfBusinessName: insured.lineOfBusiness,
    carrierName: insured.carrierName,
    premium: stripMoney(insured.policyPremium),
    quoteNumber: insured.quoteNumber,
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/insertquote?ncToken=${getNcToken()}`, payload, authHeaders());
    const id = res.data?.policiesOrQuotes?.[0]?.policyOrQuoteId || '';
    return { ok: true, quoteId: id, result: { label: 'Quote', success: true, message: 'Quote Created Successfully.' } };
  } catch {
    return { ok: false, quoteId: '', result: { label: 'Quote', success: false, message: 'Error while quote creating the request.' } };
  }
}

async function createPolicy(insuredId: string, insured: InsuredData): Promise<{ ok: boolean; policyId: string; result: SubmitStepResult }> {
  const payload = {
    insuredDatabaseId: insuredId,
    firstName: insured.firstName,
    lastName: insured.lastName,
    number: insured.policyNumber,
    effectiveDate: fmtDate(insured.effectiveDate),
    expirationDate: fmtDate(insured.expirationDate),
    bindDate: '',
    businessType: 0,
    billingType: 0,
    mgaName: '',
    carrierName: insured.carrierName,
    premium: stripMoney(insured.policyPremium),
    lineOfBusinessName: (insured.lineOfBusiness || '').replace(/,/g, ''),
    isPolicyRenewal: false,
    parentPolicyId: '',
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/addpolicy?nowCertsToken=${getNcToken()}`, payload, authHeaders());
    const id = res.data?.policiesOrQuotes?.[0]?.policyOrQuoteId || '';
    return { ok: true, policyId: id, result: { label: 'Policy', success: true, message: 'Policy has been created successfully in NowCerts' } };
  } catch {
    return { ok: false, policyId: '', result: { label: 'Policy', success: false, message: 'Unknown error while adding new policy' } };
  }
}

async function insertPolicyEndorsementFee(policyId: string, insured: InsuredData, taxesAndFees: FeeItem[]): Promise<SubmitStepResult | null> {
  const nowIso = new Date().toJSON();
  const fees: any[] = [];
  if (taxesAndFees.length > 0) {
    taxesAndFees.forEach((item) => {
      const amount = parseAmount(item.descriptionAmount);
      if (amount > 0) {
        const entry: any = { order: 1, policyId, amount, date: nowIso, premiumType: 1, type: item.mappingBillingTypeValue, followUpDate: nowIso };
        if (item.mappingBillingTypeValue === OTHER_FEES_BILLING_VALUE) entry.description = item.descriptionField;
        fees.push(entry);
      }
    });
  } else {
    const otherFees = parseAmount(insured.fees);
    const otherTaxes = parseAmount(insured.taxes);
    if (otherFees > 0) fees.push({ order: 1, policyId, amount: otherFees, date: nowIso, premiumType: 1, type: 3, followUpDate: nowIso });
    if (otherTaxes > 0) fees.push({ order: 1, policyId, amount: otherTaxes, date: nowIso, premiumType: 1, type: 4, followUpDate: nowIso });
  }
  if (fees.length === 0) return null;
  try {
    await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insertpolicyendorsementfee?ncToken=${getNcToken()}`, fees, authHeaders());
    return { label: 'Fees & Taxes', success: true, message: 'Fees and taxes added in NowCerts.' };
  } catch {
    return { label: 'Fees & Taxes', success: false, message: 'Unknown error while adding policy endorsement fee.' };
  }
}

function buildCoverageRequest(lobList: Array<{ lineOfBusinessName: string; lineOfBusinessId: string }>, lineOfBusiness: string, coverageLimitDetails: any) {
  const req: any[] = [];
  const lob = (lineOfBusiness || '').toUpperCase();
  lobList.forEach((item) => {
    const name = (item.lineOfBusinessName || '').toUpperCase();
    if (name.includes('GENERAL LIABILITY') || lob.includes('BUSINESS') || lob.includes('BUSINESS OWNERS')) {
      req.push({ generalLiability: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('WORKERS COMPENSATION')) {
      req.push({ workerCompensationAndEmployersLiability: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('AUTO')) {
      req.push({ autoMobileLiability: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('PHYSICAL DAMAGE')) {
      req.push({ physicalDamage: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('CARGO')) {
      req.push({ cargo: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('HOMEOWNERS') || lob.includes('COMMERCIAL PROPERTY') || name.includes('HOME') || name.includes('DWELLING')) {
      req.push({ homeOwnerCoverage: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    } else if (name.includes('CYBER')) {
      req.push({ customCoverages: coverageLimitDetails, lineOfBusinessDatabaseId: item.lineOfBusinessId });
    }
  });
  return req;
}

async function insertCoverageAndLimits(policyId: string, lobList: any[], lineOfBusiness: string, coverageLimitDetails: any): Promise<SubmitStepResult | null> {
  const req = buildCoverageRequest(lobList, lineOfBusiness, coverageLimitDetails);
  if (req.length === 0) return null;
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/policycoverage?ncToken=${getNcToken()}`, {
      policyDatabaseId: policyId,
      policyCoverages: [sanitizeDollar(req[0])],
    }, authHeaders());
    if (res.data?.status === 1) {
      return { label: 'Coverage & Limits', success: true, message: 'Policy coverage and limits added in NowCerts.' };
    }
    return { label: 'Coverage & Limits', success: false, message: res.data?.message || 'Unknown error while adding coverage and limits.' };
  } catch {
    return { label: 'Coverage & Limits', success: false, message: 'Unknown error while adding coverage and limits.' };
  }
}

async function getLobAndInsertCoverage(policyId: string, lineOfBusiness: string, coverageLimitDetails: any): Promise<SubmitStepResult | null> {
  if (!hasCoverageData(coverageLimitDetails)) return null;
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/loblist?policyId=${policyId}&ncToken=${getNcToken()}`, null, authHeaders());
    const list = Array.isArray(res.data) ? res.data : [];
    const matched = list.some((item: any) => (item.lineOfBusinessName || '').toUpperCase().includes((lineOfBusiness || '').toUpperCase()));
    if (!matched) return null;
    return await insertCoverageAndLimits(policyId, list, lineOfBusiness, coverageLimitDetails);
  } catch {
    return { label: 'Coverage & Limits', success: false, message: 'Unknown error while getting line of business id.' };
  }
}

async function updateSmartScan(ctx: SubmitCtx, insuredId: string, policyId: string): Promise<void> {
  const insured = ctx.insured;
  const requestJson = {
    firstName: insured.firstName, lastName: insured.lastName, email: insured.email,
    phoneNumber: insured.phoneNumber, policyNumber: insured.policyNumber, policyPremium: insured.policyPremium,
    carrierName: insured.carrierName, lineOfBusiness: insured.lineOfBusiness, quoteNumber: insured.quoteNumber,
    effectiveDate: insured.effectiveDate, expirationDate: insured.expirationDate, businessName: insured.businessName,
    addressLine1: insured.address, city: insured.city, state: insured.state, zipCode: insured.zipCode,
    fees: insured.fees, taxes: insured.taxes, taxesAndFees: ctx.taxesAndFees,
    coverageLimitDetails: ctx.coverageLimitDetails, insuredType: insured.insuredType,
    insuredDatabaseId: insuredId, policyDatabaseId: policyId,
    isYesPolicyRenewal: false, isNoPolicyRenewal: true, selectedPolicyDetails: JSON.stringify([]),
  };
  const body = {
    submissionId: ctx.submissionId,
    createProspect: ctx.isProspect && ctx.createNewClient,
    createQuote: ctx.createQuote,
    requestJson: JSON.stringify(requestJson),
    isUpdateInNowCerts: true,
  };
  await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/updatesmartscan`, body, authHeaders());
}

export async function submitAcordSmartScanToNowCerts(
  ctx: SubmitCtx,
  onStep?: (step: SubmitStepResult) => void,
): Promise<{ results: SubmitStepResult[]; insuredId: string; policyId: string; ncUrl: string; createdNewClient: boolean }> {
  const results: SubmitStepResult[] = [];
  const emit = (step: SubmitStepResult) => { results.push(step); onStep?.(step); };
  let insuredId = ctx.insuredId;
  let policyId = '';
  let ncUrl = '';
  let createdNewClient = false;

  if (ctx.createNewClient) {
    const r = ctx.isProspect ? await createNewProspect(ctx.insured) : await createNewInsured(ctx.insured);
    if (r.ok) { insuredId = r.insuredId; ncUrl = r.ncUrl; createdNewClient = true; }
    emit(r.result);
  }

  if (ctx.createQuote) {
    const r = await insertQuote(insuredId, ctx.insured);
    emit(r.result);
    if (r.ok && ctx.isProspect) {
      /* Prospect flow has no separate policy step, so fees/coverage attach to
       * the quote — mirrors the `!props.createNewPolicy` branch inside
       * insertQuote() in the legacy code (createNewPolicy is never true for
       * Prospect). */
      policyId = r.quoteId;
      if (isDecOrQuote(ctx.documentType)) {
        const feeResult = await insertPolicyEndorsementFee(policyId, ctx.insured, ctx.taxesAndFees);
        if (feeResult) emit(feeResult);
        const covResult = await getLobAndInsertCoverage(policyId, ctx.insured.lineOfBusiness, ctx.coverageLimitDetails);
        if (covResult) emit(covResult);
      }
    }
  }

  if (!ctx.isProspect) {
    /* Insured flow always creates a new policy (the "use existing policy" path
     * was removed) — fees/coverage attach to that policy. */
    const r = await createPolicy(insuredId, ctx.insured);
    emit(r.result);
    if (r.ok) {
      policyId = r.policyId;
      if (isDecOrQuote(ctx.documentType)) {
        const feeResult = await insertPolicyEndorsementFee(policyId, ctx.insured, ctx.taxesAndFees);
        if (feeResult) emit(feeResult);
        const covResult = await getLobAndInsertCoverage(policyId, ctx.insured.lineOfBusiness, ctx.coverageLimitDetails);
        if (covResult) emit(covResult);
      }
    }
  }

  if (ctx.vehicleDetails.length > 0) {
    const requestDataList = ctx.vehicleDetails.map((item: any) => ({
      insured_database_id: insuredId,
      vinNumber: item.Vin,
      vehicleManufacturer: item.Make,
      vehicleType: item.BodyType,
      year: parseInt(item.Year, 10),
      model: item.Model,
      check: true,
    }));
    try {
      await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/addvehicles?&ncToken=${getNcToken()}`, requestDataList, authHeaders());
      emit({ label: 'Vehicles', success: true, message: 'Vehicle details added successfully in NowCerts AMS.' });
    } catch {
      emit({ label: 'Vehicles', success: false, message: 'Error while adding vehicle data.' });
    }
  }

  if (ctx.driverDetails.length > 0) {
    const requestDataList = ctx.driverDetails.map((item: any) => ({
      insured_database_id: insuredId,
      first_name: item.FirstName,
      last_name: item.LastName,
      license_number: item.LicenseNumber,
      date_of_birth: item.DateOfBirth,
      active: true,
    }));
    try {
      await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/adddrivers?&ncToken=${getNcToken()}`, requestDataList, authHeaders());
      emit({ label: 'Drivers', success: true, message: 'Driver details added successfully in NowCerts AMS.' });
    } catch {
      emit({ label: 'Drivers', success: false, message: 'Error while adding driver data.' });
    }
  }

  const propertyArray = Array.isArray(ctx.propertyDetails) ? ctx.propertyDetails : (ctx.propertyDetails ? [ctx.propertyDetails] : []);
  const validProperties = propertyArray.filter(
    (item: any) => item && Object.values(item).some((v) => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== '')),
  );
  if (validProperties.length > 0) {
    try {
      await Promise.all(validProperties.map((item: any) => axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/addproperty?ncToken=${getNcToken()}`, {
        insuredDatabaseId: insuredId,
        addressLine1: item.AddressLine1,
        City: item.City,
        State: item.State,
        zipCode: item.ZipCode,
        propertyUse: item.PropertyUseCode,
      }, authHeaders())));
      emit({ label: 'Property', success: true, message: 'Property details added successfully in NowCerts AMS.' });
    } catch {
      emit({ label: 'Property', success: false, message: 'Error while adding property data.' });
    }
  }

  try {
    await updateSmartScan(ctx, insuredId, policyId);
  } catch { /* matches legacy: only shown as a toast, doesn't block the flow */ }

  return { results, insuredId, policyId, ncUrl, createdNewClient };
}
